import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OptionRS } from 'src/app/core/models/products/option-rs';
import { OptionValueRS } from 'src/app/core/models/products/option-value-rs';
import { generateOpaqueId } from 'src/app/core/utils/opaque-id.util';

/**
 * Editor de valores de opción (chips). Reemplaza el ngx-chips anterior por un editor propio con
 * mejor UX: el valor se confirma de VARIAS formas (botón "Añadir" visible, Enter, coma, o al perder
 * el foco), pegar "S, M, L" crea varios chips de golpe, y se evitan duplicados. Solo se usa en el
 * editor de variantes.
 *
 * Contrato (sin cambios para el padre): añade/quita en `optionsObject.values` y emite
 * `newSelectEvent` en ambos casos para que el padre reconcilie variantes. F3-1: identidad estable —
 * cada valor recibe un `id` opaco ({@link generateOpaqueId}), nunca derivado del texto.
 */
@Component({
  selector: 'app-dynamic-input-tags',
  templateUrl: './dynamic-input-tags.component.html',
  styleUrls: ['./dynamic-input-tags.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DynamicInputTagsComponent implements OnInit {
  @Input() optionsObject: OptionRS = {};
  @Input() label: string;
  @Input() itemsList: Array<OptionValueRS>;
  @Input() key: string;
  @Input() disableInput = false;
  @Input() showLabel = true;

  @Output() newSelectEvent = new EventEmitter<OptionValueRS>();

  draft = '';
  placeholder: string;

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.setPlaceholder();
    this.sanitizeValues();
    this.translate.onLangChange.subscribe(() => this.setPlaceholder());
  }

  /** Elimina valores vacíos/en-blanco heredados (evita chips fantasma con solo la ✕). */
  private sanitizeValues(): void {
    const vals = this.optionsObject && this.optionsObject.values;
    if (!vals || !vals.length) return;
    const cleaned = vals.filter((v) => v && (v.value || '').trim().length > 0);
    if (cleaned.length !== vals.length) {
      this.optionsObject.values = cleaned;
    }
  }

  private setPlaceholder(): void {
    this.placeholder = this.translate.instant('pv_enter_new_tag');
  }

  get values(): Array<OptionValueRS> {
    return this.optionsObject?.values || [];
  }

  get canAdd(): boolean {
    return !this.disableInput && !!this.draft.trim();
  }

  /** Confirma el texto pendiente (botón/Enter/coma/blur). Soporta separar por coma/salto/;. */
  commitDraft(): void {
    if (this.disableInput) return;
    const raw = this.draft;
    this.draft = '';
    this.addValues(raw);
  }

  private addValues(raw: string): void {
    (raw || '')
      .split(/[,\n;]+/)
      .map((p) => p.trim())
      .filter(Boolean)
      .forEach((text) => this.addOne(text));
  }

  private addOne(text: string): void {
    if (!text) return;
    // Dedup case-insensitive: no crea un valor si ya existe uno con el mismo texto.
    const exists = this.values.some((v) => (v.value || '').trim().toLowerCase() === text.toLowerCase());
    if (exists) return;
    if (!this.optionsObject.values) this.optionsObject.values = [];
    const value: OptionValueRS = { id: generateOpaqueId('ov'), value: text };
    this.optionsObject.values.push(value);
    this.newSelectEvent.emit(value);
  }

  remove(value: OptionValueRS): void {
    if (this.disableInput) return;
    const i = this.optionsObject.values ? this.optionsObject.values.indexOf(value) : -1;
    if (i > -1) {
      this.optionsObject.values.splice(i, 1);
      // El padre reconcilia variantes tanto al añadir como al quitar (mismo output).
      this.newSelectEvent.emit(value);
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      this.commitDraft();
    } else if (event.key === 'Backspace' && !this.draft && this.values.length > 0) {
      // Backspace con el input vacío borra el último chip (patrón estándar de editores de tags).
      this.remove(this.values[this.values.length - 1]);
    }
  }

  onBlur(): void {
    if (this.draft.trim()) {
      this.commitDraft();
    }
  }

  onPaste(event: ClipboardEvent): void {
    const text = event.clipboardData ? event.clipboardData.getData('text') : '';
    if (/[,\n;]/.test(text)) {
      event.preventDefault();
      this.draft = '';
      this.addValues(text);
    }
  }

  trackByValueId(_index: number, value: OptionValueRS): string {
    return value?.id || String(_index);
  }
}
